import { GROUND_SPECS, GroundListQuery } from "@playarena/shared";
import type React from "react";

import { COUNTRIES } from "../../../utils/constants";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Slider } from "../../ui/slider";

interface GroundFilterProps {
  filters: GroundListQuery;
  onFiltersChange: (filters: GroundListQuery) => void;
}

const GroundFilter: React.FC<GroundFilterProps> = ({ filters, onFiltersChange }) => {
  const amenitiesList = [
    "WiFi",
    "Parking",
    "Cafeteria",
    "Changing Rooms",
    "First Aid",
    "Security",
    "Lighting",
    "Sound System",
  ];

  const handleFilterChange = (key: keyof GroundListQuery, value: any) => {
    onFiltersChange({
      ...filters,
      [key]: value,
    });
  };

  const handleAmenityChange = (amenity: string, checked: boolean) => {
    const currentAmenities = filters.amenities || [];
    const newAmenities = checked
      ? [...currentAmenities, amenity]
      : currentAmenities.filter(a => a !== amenity);

    handleFilterChange("amenities", newAmenities.length > 0 ? newAmenities : undefined);
  };

  const handlePriceRangeChange = (values: number[]) => {
    onFiltersChange({
      ...filters,
      minPrice: values[0] || (filters.minPrice === undefined ? 0 : filters.minPrice),
      maxPrice: values[1] || (filters.maxPrice === undefined ? 1000 : filters.maxPrice),
    });
  };

  const handleCapacityRangeChange = (values: number[]) => {
    onFiltersChange({
      ...filters,
      minCapacity: values[0] || (filters.minCapacity === undefined ? 0 : filters.minCapacity),
      maxCapacity: values[1] || (filters.maxCapacity === undefined ? 100000 : filters.maxCapacity),
    });
  };

  const clearFilters = () => {
    onFiltersChange({});
  };

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <h3 className='text-lg font-semibold'>Filters</h3>
        <Button variant='ghost' size='sm' onClick={clearFilters}>
          Clear All
        </Button>
      </div>

      <div className='grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4'>
        {/* Country */}
        <div className='space-y-2'>
          <Label>Country</Label>
          <Select
            value={filters.country || "all"}
            onValueChange={value =>
              handleFilterChange("country", value === "all" ? undefined : value)
            }
          >
            <SelectTrigger>
              <SelectValue placeholder='Select country' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All Countries</SelectItem>
              {COUNTRIES.map(country => (
                <SelectItem key={country.code} value={country.code}>
                  {country.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* City */}
        <div className='space-y-2'>
          <Label htmlFor='city'>City</Label>
          <Input
            id='city'
            placeholder='Enter city'
            value={filters.city || ""}
            onChange={e => handleFilterChange("city", e.target.value || undefined)}
          />
        </div>

        {/* Ground Type */}
        <div className='space-y-2'>
          <Label>Ground Type</Label>
          <Select
            value={filters.type || "all"}
            onValueChange={value => handleFilterChange("type", value === "all" ? undefined : value)}
          >
            <SelectTrigger>
              <SelectValue placeholder='Select type' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All Types</SelectItem>
              {(Object.keys(GROUND_SPECS) as (keyof typeof GROUND_SPECS)[]).map(
                (specieKey, index) => (
                  <SelectItem
                    key={`specie_${index}_${GROUND_SPECS[specieKey]}`}
                    className='capitalize'
                    value={GROUND_SPECS[specieKey]}
                  >
                    {GROUND_SPECS[specieKey]}
                  </SelectItem>
                )
              )}
            </SelectContent>
          </Select>
        </div>

        {/* Price Range */}
        <div className='space-y-2'>
          <Label>Price Range (per hour)</Label>
          <div className='px-2'>
            <Slider
              value={[filters.minPrice || 0, filters.maxPrice || 1000]}
              onValueChange={handlePriceRangeChange}
              max={1000}
              step={10}
              className='w-full'
            />
            <div className='mt-1 flex justify-between text-sm text-muted-foreground'>
              <span>${filters.minPrice || 0}</span>
              <span>${filters.maxPrice || 1000}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Capacity Range */}
      <div className='space-y-2'>
        <Label>Capacity Range</Label>
        <div className='px-2'>
          <Slider
            value={[filters.minCapacity || 0, filters.maxCapacity || 100000]}
            onValueChange={handleCapacityRangeChange}
            max={100000}
            step={1000}
            className='w-full'
          />
          <div className='mt-1 flex justify-between text-sm text-muted-foreground'>
            <span>{(filters.minCapacity || 0).toLocaleString()}</span>
            <span>{(filters.maxCapacity || 100000).toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Amenities */}
      <div className='space-y-3'>
        <Label>Amenities</Label>
        <div className='grid grid-cols-2 gap-3 md:grid-cols-4'>
          {amenitiesList.map(amenity => (
            <div key={amenity} className='flex items-center space-x-2'>
              <Checkbox
                id={amenity}
                checked={filters.amenities?.includes(amenity) || false}
                onCheckedChange={checked => handleAmenityChange(amenity, checked as boolean)}
              />
              <Label htmlFor={amenity} className='text-sm'>
                {amenity}
              </Label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GroundFilter;
